import type { Challenge } from '../data/types';

export type ShareOutcome = 'shared' | 'copied' | 'saved' | 'cancelled' | 'unsupported';

/** The line of text that travels with the link (design/04 "Sharing"). */
export function shareText(challenge: Challenge, invite: boolean): string {
  return invite
    ? `${challenge.title} · your turn, ${challenge.minutes} minutes`
    : `${challenge.title} · try this one, ${challenge.minutes} minutes`;
}

/**
 * Share the link, not the PNG and the link: one tappable bubble in a chat.
 *
 * The url goes in `text` as well as in `url`, because a share sheet hands each
 * target one item and picks for itself which. iOS's Copy takes the text, and
 * with the link only in `url` it copied a sentence with nothing to tap —
 * exactly what an interview turned up on 2026-09-18. Repeating it costs a
 * target that reads both a duplicated link; losing it costs the share.
 */
export async function shareLink(url: string, text: string): Promise<ShareOutcome> {
  if (typeof navigator !== 'undefined' && navigator.share) {
    try { await navigator.share({ text: `${text}\n${url}`, url }); return 'shared'; }
    catch (err) { if ((err as Error).name === 'AbortError') return 'cancelled'; }
  }
  try { await navigator.clipboard.writeText(`${text}\n${url}`); return 'copied'; }
  catch { return 'unsupported'; }
}

/** Save image: the second action, for stories and camera rolls where there is no link. */
export async function saveImage(blob: Blob, filename: string): Promise<ShareOutcome> {
  const file = new File([blob], filename, { type: 'image/png' });
  if (typeof navigator !== 'undefined' && navigator.canShare?.({ files: [file] })) {
    try { await navigator.share({ files: [file] }); return 'shared'; }
    catch (err) { if ((err as Error).name === 'AbortError') return 'cancelled'; }
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  return 'saved';
}

/**
 * Save a picture from a URL to the camera roll.
 *
 * The counterpart to sharing a link: a file belongs where a file is the point —
 * a story, a camera roll — and a link belongs in a chat, because it is the only
 * one of the two that can be opened (owner, 2026-09-19, #99). A picture that
 * will not download (a bucket without CORS) reports rather than failing
 * silently.
 */
export async function savePicture(url: string, filename: string): Promise<ShareOutcome> {
  try {
    const blob = await fetch(url).then((r) => (r.ok ? r.blob() : Promise.reject(r.status)));
    return await saveImage(blob, filename);
  } catch {
    return 'unsupported';
  }
}
